"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  MODAL_IDS,
  ResponsiveCommandSelect,
  ResponsiveModal,
  useUniversalAuth,
} from "@wtc/ui";
import { CheckCircle2, ExternalLink, FileText, Loader2 } from "lucide-react";
import type { Message } from "@/lib/core/crm/types";
import { useInboxes } from "@/lib/hooks/messages/useInboxes";

interface SlackChannel {
  id: string;
  name: string;
  is_member?: boolean;
  is_private?: boolean;
}

interface ExportThreadToSlackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: Message;
  globalModalId?: string;
}

export function ExportThreadToSlackModal({
  open,
  onOpenChange,
  message,
  globalModalId = MODAL_IDS.messages.exportThreadToSlack,
}: ExportThreadToSlackModalProps) {
  const { session } = useUniversalAuth();
  const { inboxes } = useInboxes();

  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [channelSearch, setChannelSearch] = useState<string>("");
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canvasUrl, setCanvasUrl] = useState<string | null>(null);
  const [exported, setExported] = useState(false);

  // The connection id lives on the inbox, not the message.
  const connectionId = useMemo(() => {
    const inbox = inboxes.find((i) => i.id === message.inboxId);
    return inbox?.connectionId ?? null;
  }, [inboxes, message.inboxId]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelectedChannel("");
    setChannelSearch("");
    setCanvasUrl(null);
    setExported(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const fetchChannels = async () => {
      try {
        setChannelsLoading(true);
        setError(null);
        const response = await fetch("/api/slack/channels");
        if (!response.ok) throw new Error("Failed to fetch Slack channels");
        const data = await response.json();
        setChannels((data.channels as SlackChannel[]) || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setChannelsLoading(false);
      }
    };
    void fetchChannels();
  }, [open]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleExport = useCallback(async () => {
    if (!selectedChannel) {
      setError("Please select a channel");
      return;
    }
    if (!connectionId) {
      setError("Could not resolve this message's mailbox connection");
      return;
    }
    if (!session?.access_token) {
      setError("No authentication token found");
      return;
    }

    setIsExporting(true);
    setError(null);
    try {
      const response = await fetch("/api/slack/export-email-thread", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          channelId: selectedChannel,
          provider: message.provider,
          messageId: message.providerMessageId,
          connectionId,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to export the email to Slack");
      }
      setCanvasUrl(typeof data.canvasUrl === "string" ? data.canvasUrl : null);
      setExported(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsExporting(false);
    }
  }, [selectedChannel, connectionId, session, message]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Export thread to Slack"
      description={`Create a Slack Canvas for: ${message.subject}`}
      globalModalId={globalModalId}
    >
      <div className="space-y-4 py-2">
        {exported ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-md bg-green-50 p-4 text-sm dark:bg-green-950/30">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <p className="font-medium text-green-800 dark:text-green-300">
                Canvas created and shared to the channel.
              </p>
            </div>
            {canvasUrl && (
              <a
                href={canvasUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Open the canvas
              </a>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-3 text-sm">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <p className="text-muted-foreground">
                Exports this email as a standalone, searchable Slack Canvas — the subject, the
                From/To/Cc/Date, and the message body, with links back into Hub — shared to the
                channel you pick.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Share Canvas to Channel</label>
              <ResponsiveCommandSelect
                value={selectedChannel}
                onValueChange={setSelectedChannel}
                options={channels.map((channel) => ({
                  value: channel.id,
                  label: `${channel.is_private ? "🔒" : "#"}${channel.name}`,
                  searchableText: channel.name,
                }))}
                placeholder={
                  channelsLoading
                    ? "Loading channels..."
                    : channels.length === 0
                      ? "No channels available"
                      : `Search ${channels.length} channels...`
                }
                searchPlaceholder="Type to search channels..."
                disabled={channelsLoading || channels.length === 0}
                searchValue={channelSearch}
                onSearchChange={setChannelSearch}
                title="Select Slack Channel"
                description="Channel members get read access to the canvas."
              />
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleClose} disabled={isExporting}>
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={isExporting || channelsLoading || !selectedChannel}
              >
                {isExporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isExporting ? "Exporting..." : "Create Canvas"}
              </Button>
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}
